import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import vasstrOrangeLogo from '../media/vasstrOrangeLogo.png';
import Top from '../components/Top';
import VFooter from '../components/VFooter';
import { Card, Button, Input, Textarea, Alert } from '../components/ui';
import COLORS from '../utils/colors';
import API_BASE_URL from '../config/api';

const ContactUs = () => {
  const nameInputRef = useRef("");
  const nameLabelRef = useRef("");
  const emailInputRef = useRef("");
  const emailLabelRef = useRef("");
  const subjectInputRef = useRef("");
  const subjectLabelRef = useRef("");
  const messageInputRef = useRef("");
  const messageLabelRef = useRef("");

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: '',
  });

  const [status, setStatus] = useState('');
 
  useEffect(() => {
    // Smooth scroll for anchor links
    const anchorLinks = document.querySelectorAll('a[href^="#"]');
    
    const handleAnchorClick = (e) => {
      e.preventDefault();
      const target = document.querySelector(e.currentTarget.getAttribute('href'));
      if (target) {
        target.scrollIntoView({ behavior: 'smooth' });
      }
    };

    anchorLinks.forEach(anchor => {
      anchor.addEventListener('click', handleAnchorClick);
    });

    return () => {
      anchorLinks.forEach(anchor => {
        anchor.removeEventListener('click', handleAnchorClick);
      });
    };
  }, []);

  const handleInputFocus = (inputRef, labelRef) => {
    if (labelRef.current) {
      labelRef.current.style.display = 'none';
    }
  };

  const handleInputBlur = (inputRef, labelRef) => {
    if (labelRef.current && inputRef.current && inputRef.current.value.trim() === '') {
      labelRef.current.style.display = 'block';
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      const response = await fetch(`${API_BASE_URL}/api/contact_us/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      const result = await response.json();
      if (response.ok) {
        setStatus('Message sent successfully!');
        setFormData({ name: '', email: '', subject: '', message: '' });
        // Show labels again after form reset
        if (nameLabelRef.current) nameLabelRef.current.style.display = 'block';
        if (emailLabelRef.current) emailLabelRef.current.style.display = 'block';
        if (subjectLabelRef.current) subjectLabelRef.current.style.display = 'block';
        if (messageLabelRef.current) messageLabelRef.current.style.display = 'block';
      } else {
        setStatus(result.error || 'Failed to send message.');
        console.error(result.error);
      }
    } catch (error) {
      setStatus('Something went wrong.');
      console.error('Error:', error);
    }
  };

  return (
    <div className="font-sans">
      <Top/>

      <section className="terms-section  bg-[#f4ece6] py-20 px-4">
        <div className="container max-w-4xl mx-auto mt-5 bg-white rounded-lg shadow-md p-8">
          <h2 className="text-black font-serif text-3xl mb-8">Contact Us</h2>
          <form onSubmit={handleSubmit}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="relative">
                <label htmlFor="name" ref={nameLabelRef} className="block text-gray-700 mb-2">
                  Name:
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  ref={nameInputRef}
                  onFocus={() => handleInputFocus(nameInputRef, nameLabelRef)}
                  onBlur={() => handleInputBlur(nameInputRef, nameLabelRef)}
                  placeholder="Your Name"
                  className="w-full p-3 border border-gray-300 rounded focus:outline-none focus:ring-2"
                  style={{ outlineColor: COLORS.AUTUMN }}
                  required
                />
              </div>
              <div className="relative">
                <label htmlFor="email" ref={emailLabelRef} className="block text-gray-700 mb-2">
                  Email:
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  ref={emailInputRef}
                  onFocus={() => handleInputFocus(emailInputRef, emailLabelRef)}
                  onBlur={() => handleInputBlur(emailInputRef, emailLabelRef)}
                  placeholder="Your Email"
                  className="w-full p-3 border border-gray-300 rounded focus:outline-none focus:ring-2"
                  style={{ outlineColor: COLORS.AUTUMN }}
                  required
                />
              </div>
            </div>

            <div className="mt-6 relative">
              <label htmlFor="subject" ref={subjectLabelRef} className="block text-gray-700 mb-2">
                Subject:
              </label>
              <input
                type="text"
                id="subject"
                name="subject"
                value={formData.subject}
                onChange={handleChange}
                ref={subjectInputRef}
                onFocus={() => handleInputFocus(subjectInputRef, subjectLabelRef)}
                onBlur={() => handleInputBlur(subjectInputRef, subjectLabelRef)}
                placeholder="Subject"
                className="w-full p-3 border border-gray-300 rounded focus:outline-none focus:ring-2"
                  style={{ outlineColor: COLORS.AUTUMN }}
                required
              />
            </div>

            <div className="mt-6 relative">
              <label htmlFor="message" ref={messageLabelRef} className="block text-gray-700 mb-2">
                Message:
              </label>
              <textarea
                id="message"
                name="message"
                value={formData.message}
                onChange={handleChange}
                ref={messageInputRef}
                onFocus={() => handleInputFocus(messageInputRef, messageLabelRef)}
                onBlur={() => handleInputBlur(messageInputRef, messageLabelRef)}
                placeholder="Your Message"
                className="w-full p-3 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-orange-500 min-h-[150px]"
                required
              ></textarea>
            </div>

            <div className="mt-6">
              <button
                type="submit"
                className="w-full py-3 text-white border-white hover:border-white rounded transition hover:opacity-90"
                style={{ backgroundColor: COLORS.AUTUMN }}
              >
                Send Message
              </button>
              {status && (
                <p className={`text-center mt-4 text-sm ${status.includes('success') ? 'text-green-600' : 'text-red-600'}`}>
                  {status}
                </p>
              )}
            </div>
          </form>
        </div>
      </section>
      <VFooter/>
    </div>
  );
};

export default ContactUs;